// Quality organisation-level settings (Admin Setup > Quality).
// CR-7293: Cost of Quality rate, group or site level.
// CR-7320: ERP fault-code -> fault category mapping.
import api, { type AxiosResponse } from './client';

export type CostOfQualityLevel = 'group' | 'site';
export type CostOfQualitySource = 'group' | 'site' | 'platform';

export interface CostOfQualitySite {
  company_id: number;
  company_name: string;
  /** The site's own rate; null = not set. */
  rate: string | null;
  /** The rate its NCRs are actually costed at. */
  effective_rate: string;
  source: CostOfQualitySource;
  can_edit: boolean;
}

export interface CostOfQualitySettings {
  organisation: { kind: 'group' | 'company'; id: number; name: string };
  level: CostOfQualityLevel;
  group_rate: string | null;
  platform_default: string;
  can_edit_group: boolean;
  sites: CostOfQualitySite[];
  /** Helix users only: every customer organisation (`group:<id>` / `company:<id>`). */
  organisations: { key: string; name: string }[];
}

export interface CostOfQualityUpdate {
  organisation?: string;
  level?: CostOfQualityLevel;
  group_rate?: string | null;
  company_id?: number;
  rate?: string | null;
}

export interface FaultCodeMapping {
  id: number;
  erp_code: string;
  erp_description: string;
  fault_category: number;
  fault_category_name: string;
  fault_group_id: number;
  fault_group_name: string;
  ncr_count: number;
  is_provisional: boolean;
  confirmed_by_name: string | null;
  confirmed_at: string | null;
  updated_at: string;
}

export interface FaultCodeMappingCategory {
  id: number;
  name: string;
  fault_group_id: number;
  fault_group_name: string;
}

export interface FaultCodeMappingContext {
  owner_name: string;
  owner_kind: 'group' | 'company';
  categories: FaultCodeMappingCategory[];
  total_rows: number;
  provisional_rows: number;
  owner_ncrs: number;
  async_threshold: number;
}

export type FaultCodeImportAction = 'create' | 'update' | 'unchanged' | 'error';

export interface FaultCodeImportRow {
  line: number;
  erp_code: string;
  erp_description: string;
  fault_group: string;
  fault_category: string;
  category_id: number | null;
  current: string | null;
  action: FaultCodeImportAction;
  message: string;
}

export interface FaultCodeImportResult {
  dry_run: boolean;
  summary: Record<FaultCodeImportAction, number>;
  rows: FaultCodeImportRow[];
  saved: { created: number; updated: number } | null;
}

export interface FaultCodeApplyResult {
  queued: boolean;
  force: boolean;
  changed?: number;
  stats?: Record<string, number>;
  unmapped?: Record<string, number>;
}

const orgParams = (organisation?: string) => (organisation ? { params: { organisation } } : {});

export const qualitySettingsAPI = {
  getCostOfQuality: (organisation?: string): Promise<AxiosResponse<CostOfQualitySettings>> =>
    api.get('/core/settings/quality/cost-of-quality/', orgParams(organisation)),

  updateCostOfQuality: (data: CostOfQualityUpdate): Promise<AxiosResponse<CostOfQualitySettings>> =>
    api.patch('/core/settings/quality/cost-of-quality/', data, orgParams(data.organisation)),

  getFaultCodeMappings: (organisation?: string): Promise<AxiosResponse<FaultCodeMapping[]>> =>
    api.get('/quality/fault-code-mappings/', orgParams(organisation)),

  getFaultCodeMappingContext: (organisation?: string): Promise<AxiosResponse<FaultCodeMappingContext>> =>
    api.get('/quality/fault-code-mappings/context/', orgParams(organisation)),

  createFaultCodeMapping: (
    data: { erp_code: string; erp_description?: string; fault_category: number },
    organisation?: string,
  ): Promise<AxiosResponse<FaultCodeMapping>> =>
    api.post('/quality/fault-code-mappings/', data, orgParams(organisation)),

  updateFaultCodeMapping: (
    id: number,
    data: Partial<Pick<FaultCodeMapping, 'erp_code' | 'erp_description' | 'fault_category'>>,
    organisation?: string,
  ): Promise<AxiosResponse<FaultCodeMapping>> =>
    api.patch(`/quality/fault-code-mappings/${id}/`, data, orgParams(organisation)),

  deleteFaultCodeMapping: (id: number, organisation?: string): Promise<AxiosResponse<void>> =>
    api.delete(`/quality/fault-code-mappings/${id}/`, orgParams(organisation)),

  importFaultCodeMappings: (
    file: File, dryRun: boolean, organisation?: string,
  ): Promise<AxiosResponse<FaultCodeImportResult>> => {
    const form = new FormData();
    form.append('file', file);
    form.append('dry_run', dryRun ? 'true' : 'false');
    return api.post('/quality/fault-code-mappings/import-csv/', form, {
      ...orgParams(organisation),
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  confirmFaultCodeMappings: (
    provisional: boolean, organisation?: string,
  ): Promise<AxiosResponse<{ changed: number; provisional: boolean }>> =>
    api.post('/quality/fault-code-mappings/confirm/', { provisional }, orgParams(organisation)),

  applyFaultCodeMappings: (force: boolean, organisation?: string): Promise<AxiosResponse<FaultCodeApplyResult>> =>
    api.post('/quality/fault-code-mappings/apply/', { force }, orgParams(organisation)),
};
